import { useMemo, useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CreditCard } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useTransactions } from "@/hooks/useTransactions";
import { useBankTransactions } from "@/hooks/useBankTransactions";
import { useDashboardRange } from "./DashboardContext";
import { useAnonymize, anonymizeCategory, anonymizeCard, anonymizeDescription, anonymizeAmount } from "@/components/AnonymizeContext";

function isDateInCurrentPeriod(date: string, range: "month" | "year") {
  if (!date) return false;
  const now = new Date();
  const d = new Date(date);
  if (range === "month") {
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  } else {
    return d.getFullYear() === now.getFullYear();
  }
}

function getCategoryColor(category: string) {
  const colorMap: Record<string, string> = {
    Coffee: "bg-yellow-100 text-yellow-700",
    Groceries: "bg-green-100 text-green-700",
    Transport: "bg-blue-100 text-blue-700",
    Entertainment: "bg-pink-100 text-pink-700",
    Rent: "bg-violet-100 text-violet-700",
    Shopping: "bg-indigo-100 text-indigo-700",
    Income: "bg-green-200 text-green-800",
    "Credit Card Payment": "bg-purple-100 text-purple-700",
  };
  return colorMap[category] || "bg-gray-100 text-gray-700";
}

type MergedTransaction = {
  source: string;
  amount: number;
  date: string;
  card: string;
  category: string;
  description: string;
  type?: string;
  id?: string;
};

export default function TransactionsTable() {
  const [search, setSearch] = useState("");
  const [cardFilter, setCardFilter] = useState<string | null>(null);
  const { data: transactions = [], isLoading: loadingTxns, error: errorTxns } = useTransactions();
  const { data: bankTransactions = [], isLoading: loadingBankTxns, error: errorBankTxns } = useBankTransactions();
  const range = useDashboardRange();
  const { anonymize } = useAnonymize();

  const isLoading = loadingTxns || loadingBankTxns;
  const error = errorTxns || errorBankTxns;

  const mergedTransactions = useMemo<MergedTransaction[]>(() => {
    return [
      ...transactions
        .filter(t => isDateInCurrentPeriod(t.date, range))
        .map(t => ({
          source: "app",
          amount: Number(t.amount),
          date: t.date,
          card: t.card || "",
          category: t.category || "",
          description: t.description || "",
          id: t.id,
          type: t.type || ""
        })),
      ...bankTransactions
        .filter(t => isDateInCurrentPeriod(t.date, range))
        .map(t => ({
          source: "bank",
          amount: Number(t.amount),
          card: t.card || "Bank ACH",
          category: t.category || "",
          description: t.description || "",
          date: t.date || "",
          type: t.type || ""
        })),
    ].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date.localeCompare(a.date);
    });
  }, [transactions, bankTransactions, range]);

  const filteredTxns = useMemo(() => {
    return mergedTransactions.filter((txn, idx) => {
      const descriptionCompare = anonymize
        ? anonymizeDescription(idx).toLowerCase()
        : txn.description.toLowerCase();
      const categoryCompare = anonymize
        ? anonymizeCategory(txn.category, idx).toLowerCase()
        : txn.category.toLowerCase();
      const matchesSearch =
        descriptionCompare.includes(search.toLowerCase()) ||
        categoryCompare.includes(search.toLowerCase());
      const matchesCard =
        cardFilter && cardFilter !== "all"
          ? (anonymize
              ? anonymizeCard(txn.card, idx) === cardFilter
              : txn.card === cardFilter)
          : true;
      return matchesSearch && matchesCard;
    });
  }, [mergedTransactions, search, cardFilter, anonymize]);

  const cardOptions = useMemo(() => {
    if (!anonymize) {
      const cards = new Set<string>();
      mergedTransactions.forEach(txn => {
        if (txn.card) cards.add(txn.card);
      });
      return Array.from(cards.values());
    }
    return mergedTransactions.map((_, idx) => anonymizeCard("", idx));
  }, [mergedTransactions, anonymize]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) return "-";
      return format(parsedDate, "MMM d, yyyy");
    } catch (error) {
      console.error("Date formatting error:", error, "for date:", dateString);
      return "-";
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow p-4 mt-6 h-48 flex items-center justify-center text-gray-400">
        Loading...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow p-4 mt-6 text-destructive">
        Error loading transactions.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow p-4 mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h3 className="font-semibold text-lg">Recent Transactions</h3>
        <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
          <input
            type="text"
            className="border rounded px-3 py-1 text-sm w-full sm:w-56 focus:ring-2 focus:ring-primary"
            placeholder="Search by description or category"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select value={cardFilter || undefined} onValueChange={setCardFilter}>
            <SelectTrigger className="w-36 bg-white border text-sm">
              <CreditCard className="mr-2 h-4 w-4 opacity-60" />
              <SelectValue placeholder="All Cards" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-white">
              <SelectItem value="all">All Cards</SelectItem>
              {cardOptions.map(card => (
                <SelectItem key={card} value={card}>
                  {card}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-gray-600 border-b">
              <th className="py-2 px-3 text-left">Date</th>
              <th className="py-2 px-3 text-left">Description</th>
              <th className="py-2 px-3 text-left">Amount</th>
              <th className="py-2 px-3 text-left">Category</th>
              <th className="py-2 px-3 text-left">Card</th>
              <th className="py-2 px-3 text-left">Type</th>
            </tr>
          </thead>
          <tbody>
            {filteredTxns.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-gray-400">
                  No transactions found.
                </td>
              </tr>
            )}
            {filteredTxns.map((t, i) => (
              <tr key={i} className="border-b last:border-b-0">
                <td className="py-2 px-3">{formatDate(t.date)}</td>
                <td className="py-2 px-3">
                  {anonymize ? anonymizeDescription(i) : t.description || "-"}
                </td>
                <td className="py-2 px-3 font-semibold">
                  {anonymize ? (
                    <span className="text-gray-500">{anonymizeAmount(t.amount)}</span>
                  ) : Number(t.amount) < 0 ? (
                    <span className="text-red-500">-${Math.abs(Number(t.amount)).toFixed(2)}</span>
                  ) : (
                    <span className="text-green-600">+${Number(t.amount).toFixed(2)}</span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <span className={"rounded px-2 py-1 font-medium whitespace-nowrap " + getCategoryColor(t.category)}>
                    {anonymize ? anonymizeCategory(t.category, i) : t.category || "-"}
                  </span>
                </td>
                <td className="py-2 px-3">
                  {anonymize ? anonymizeCard(t.card, i) : t.card || "-"}
                </td>
                <td className="py-2 px-3">{t.type || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
